import { useEffect, useState } from "react";
import {
  type EvccConfigBody,
  type EvccConfigResponse,
  fetchEvccConfig,
  fetchEvccTariffUrls,
  type EvccTariffUrls,
  resetEvccConfig,
  saveEvccConfig,
} from "../api";
import { SectionTitle } from "./ui";

/**
 * Account-section: EV charging via evcc. Same override precedent as
 * PricingSettings - the installer sets a default, the customer can
 * override (stored via the backend on ems-engine's PVC), "Terug naar
 * standaard" reverts. When enabled, the Slim Stroom planner ingests
 * evcc's charging plan and the live overview shows the Auto branch.
 */
export function EvSettings() {
  const [state, setState] = useState<EvccConfigResponse | null>(null);
  const [form, setForm] = useState<EvccConfigBody>({
    enabled: false,
    url: "",
    behind_meter: true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  useEffect(() => {
    fetchEvccConfig()
      .then((s) => {
        setState(s);
        setForm({ enabled: s.enabled, url: s.url, behind_meter: s.behind_meter });
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  function patch(p: Partial<EvccConfigBody>) {
    setForm((f) => ({ ...f, ...p }));
  }

  async function onSave() {
    setBusy(true);
    setError(null);
    try {
      const s = await saveEvccConfig(form);
      setState(s);
      setForm({ enabled: s.enabled, url: s.url, behind_meter: s.behind_meter });
      setSavedFlash("Opgeslagen");
      window.setTimeout(() => setSavedFlash(null), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onReset() {
    setBusy(true);
    setError(null);
    try {
      const s = await resetEvccConfig();
      setState(s);
      setForm({ enabled: s.enabled, url: s.url, behind_meter: s.behind_meter });
      setSavedFlash("Teruggezet naar standaard");
      window.setTimeout(() => setSavedFlash(null), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flex flex-col gap-2">
      <SectionTitle>EV-laden (evcc)</SectionTitle>
      <div className="card p-4 flex flex-col gap-3">
        <label className="flex items-start justify-between gap-3 cursor-pointer">
          <span className="min-w-0">
            <span className="text-sm font-medium block">EV-laden via evcc</span>
            <span className="text-xs text-ink-mute block mt-0.5">
              SlimStroom leest de laadplanning van je evcc en toont de
              auto op het live-overzicht. Zet uit als je geen EV of
              evcc hebt.
            </span>
          </span>
          <input
            type="checkbox"
            className="mt-1 h-5 w-5 accent-[var(--color-accent)] shrink-0"
            checked={form.enabled}
            onChange={(e) => patch({ enabled: e.target.checked })}
          />
        </label>

        {form.enabled && (
          <>
            <label className="block">
              <span className="text-sm font-medium">evcc-adres (URL)</span>
              <input
                type="url"
                inputMode="url"
                placeholder="http://192.168.1.10:7070"
                className="mt-1 w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm font-mono"
                value={form.url}
                onChange={(e) => patch({ url: e.target.value })}
              />
              <span className="text-xs text-ink-mute block mt-1">
                SlimStroom leest dit adres vanuit onze centrale omgeving. Zet de evcc-poort niet
                rechtstreeks open op internet: laat alleen ons adres toe of gebruik een reverse proxy
                met een geheim pad en https. Zie de uitleg onder Help → Elektrische auto.
              </span>
            </label>

            <label className="flex items-start justify-between gap-3 cursor-pointer">
              <span className="min-w-0">
                <span className="text-sm font-medium block">
                  Laadpaal wordt gemeten door de Victron-installatie
                </span>
                <span className="text-xs text-ink-mute block mt-0.5">
                  Meestal aan. Zet uit als de laadpaal een eigen
                  aansluiting of groep heeft die niet via de
                  Victron-meter loopt - anders trekt de planner het
                  laden dubbel van je huisverbruik af.
                </span>
              </span>
              <input
                type="checkbox"
                className="mt-1 h-5 w-5 accent-[var(--color-accent)] shrink-0"
                checked={form.behind_meter}
                onChange={(e) => patch({ behind_meter: e.target.checked })}
              />
            </label>
          </>
        )}

        <TariffFeed />

        {error && <div className="text-sm text-danger">{error}</div>}

        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            disabled={busy || state?.source !== "override"}
            onClick={onReset}
            className="text-sm text-ink-mute disabled:opacity-40 hover:text-ink"
          >
            Terug naar standaard
          </button>
          <div className="flex items-center gap-3">
            {savedFlash && <span className="text-xs text-accent">{savedFlash}</span>}
            <button
              type="button"
              disabled={busy}
              onClick={onSave}
              className="bg-accent text-white text-sm rounded-full px-4 py-2 font-medium disabled:opacity-60"
            >
              Opslaan
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}


// Onze stroomprijzen (bekend + verwachting, met het eigen contract) als
// tarief in evcc: de klant plakt de URL in een "User-defined" tarief
// met het forecast-plugin. Token zit in de URL, geen login nodig voor
// evcc. docs/customer-app.md > evcc tariff feed.
function TariffFeed() {
  const [urls, setUrls] = useState<EvccTariffUrls | null>(null);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    fetchEvccTariffUrls().then((u) => alive && setUrls(u)).catch(() => undefined);
    return () => { alive = false; };
  }, []);
  if (!urls) return null;
  async function copy(label: string, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      setCopied(null);
    }
  }
  const snippet = (uri: string) => `forecast:\n  source: http\n  uri: ${uri}`;
  return (
    <div className="border-t border-line pt-3 flex flex-col gap-2">
      <button type="button" onClick={() => setOpen(!open)} className="text-left">
        <span className="text-sm font-medium block">Stroomprijzen in evcc</span>
        <span className="text-xs text-ink-mute block mt-0.5">
          Laat evcc met dezelfde prijzen rekenen als SlimStroom: de bekende
          dagprijzen plus de verwachting tot zo'n twaalf dagen vooruit, met uw eigen contract. {open ? "Verberg" : "Toon de koppeling"}.
        </span>
      </button>
      {open && (
        <div className="flex flex-col gap-3 text-xs">
          {[["Inkoopprijs (grid)", urls.grid_url], ["Terugleverprijs (feedin)", urls.feedin_url]].map(([label, url]) => (
            <div key={label} className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="font-medium">{label}</span>
                <button type="button" onClick={() => copy(label, snippet(url))}
                  className="text-accent">
                  {copied === label ? "Gekopieerd" : "Kopieer evcc-instelling"}
                </button>
              </div>
              <pre className="rounded-lg bg-card-soft p-2 overflow-x-auto font-mono text-[11px] whitespace-pre">{snippet(url)}</pre>
            </div>
          ))}
          <p className="text-ink-mute">
            In evcc: Configuratie, Tarieven, Netprijs (of Terugleverprijs), apparaat
            "Door gebruiker gedefinieerd", en plak de regels hierboven. evcc haalt de
            prijzen elk uur op; de URL is persoonlijk, deel hem niet. Prijzen per{" "}
            {urls.resolution === "15min" ? "kwartier" : "uur"}, inclusief belasting en btw.
          </p>
        </div>
      )}
    </div>
  );
}
